/**
 * Knowledge Brain page — Patch 27.
 *
 * Three-column workspace: Sources · Approval Queue · Approved Entries.
 * The owner uploads a document or submits a URL, reviews the parsed
 * chunks, and approves/rejects each one. Nothing enters the brain
 * automatically — every learning event requires an explicit approval.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileUp, Globe, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

import { useCurrentWorkspace } from "@/entities/workspace";
import {
  approveChunk,
  getSummary,
  ingestUrl,
  KIND_LABEL,
  listEntries,
  listQueue,
  listSources,
  rejectChunk,
  uploadDocument,
  type KnowledgeChunk,
  type KnowledgeEntry,
  type KnowledgeEntryKind,
} from "@/entities/knowledge";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { PremiumEmpty } from "@/shared/ui/premium-empty";
import { Skeleton } from "@/shared/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";


export default function KnowledgePage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  if (!workspace) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16">
        <PremiumEmpty testId="knowledge-empty" title="Select a workspace"
          description="The Knowledge Brain lives inside a workspace. Choose one from the switcher above." />
      </div>
    );
  }
  return <KnowledgeInner ws={workspace.id} />;
}


function KnowledgeInner({ ws }: { ws: string }): JSX.Element {
  const qc = useQueryClient();
  const summary = useQuery({ queryKey: ["knowledge", "summary", ws], queryFn: () => getSummary(ws) });
  const sources = useQuery({ queryKey: ["knowledge", "sources", ws], queryFn: () => listSources(ws) });
  const queue = useQuery({ queryKey: ["knowledge", "queue", ws], queryFn: () => listQueue(ws) });
  const entries = useQuery({ queryKey: ["knowledge", "entries", ws], queryFn: () => listEntries(ws) });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["knowledge"] });
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-8 md:px-8" data-testid="knowledge-page">
      <header className="space-y-2">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <Sparkles className="h-3 w-3" data-testid="knowledge-brain-icon" />
          <span>Knowledge Brain · Patch 27</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl">Teach your Marketing Department</h1>
        <p className="text-sm text-muted-foreground md:max-w-2xl">
          Upload documents or drop a URL. The system parses them into approval-ready facts.
          Nothing enters your Knowledge Brain until you approve it — you're always in control of what it learns.
        </p>
      </header>

      <SummaryStrip summary={summary.data} />

      <div className="grid gap-6 md:grid-cols-2">
        <IngestUploadCard ws={ws} onIngested={invalidateAll} />
        <IngestUrlCard ws={ws} onIngested={invalidateAll} />
      </div>

      <Tabs defaultValue="queue" className="w-full">
        <TabsList data-testid="knowledge-tablist">
          <TabsTrigger value="queue" data-testid="knowledge-tab-queue">
            Awaiting approval
            {summary.data && summary.data.chunks_awaiting_approval > 0 && (
              <Badge className="ml-2" variant="outline">{summary.data.chunks_awaiting_approval}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="entries" data-testid="knowledge-tab-entries">
            Approved knowledge
            {summary.data && summary.data.entries_active > 0 && (
              <Badge className="ml-2" variant="outline">{summary.data.entries_active}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sources" data-testid="knowledge-tab-sources">Sources</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-4">
          <ApprovalQueue ws={ws} chunks={queue.data ?? []} loading={queue.isLoading} onChange={invalidateAll} />
        </TabsContent>

        <TabsContent value="entries" className="mt-4">
          <EntriesList entries={entries.data ?? []} loading={entries.isLoading} />
        </TabsContent>

        <TabsContent value="sources" className="mt-4">
          <SourcesList sources={sources.data ?? []} loading={sources.isLoading} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function SummaryStrip({ summary }: { summary?: { sources_total: number; chunks_awaiting_approval: number; entries_active: number; entries_by_kind: Record<string, number> } }) {
  if (!summary) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
      </div>
    );
  }
  const kinds = Object.entries(summary.entries_by_kind ?? {}).sort((a, b) => b[1] - a[1]).slice(0, 3);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="knowledge-summary">
      <StatTile label="Sources" value={summary.sources_total} testId="knowledge-stat-sources" />
      <StatTile label="Awaiting approval" value={summary.chunks_awaiting_approval} testId="knowledge-stat-queue" accent={summary.chunks_awaiting_approval > 0 ? "amber" : undefined} />
      <StatTile label="In the brain" value={summary.entries_active} testId="knowledge-stat-entries" />
      <StatTile
        label="Top kinds"
        value={kinds.length ? kinds.map(([k, n]) => `${KIND_LABEL[k as KnowledgeEntryKind] ?? k} · ${n}`).join(" / ") : "—"}
        testId="knowledge-stat-kinds"
        smallValue
      />
    </div>
  );
}


function StatTile({ label, value, testId, accent, smallValue }: { label: string; value: number | string; testId?: string; accent?: "amber"; smallValue?: boolean }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-md border p-4 ${accent === "amber" ? "border-amber-500/40 bg-amber-500/5" : "border-border/60 bg-card/60"}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={smallValue ? "mt-1 text-sm" : "mt-1 font-display text-2xl"}>{value}</div>
    </div>
  );
}


function IngestUploadCard({ ws, onIngested }: { ws: string; onIngested: () => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  const mutation = useMutation({
    mutationFn: (file: File) => uploadDocument(ws, file),
    onSuccess: (r) => {
      toast.success(`Parsed ${r.chunks_produced} fact${r.chunks_produced === 1 ? "" : "s"} — review them below.`);
      onIngested();
    },
    onError: (e: Error) => toast.error(e.message || "Upload failed"),
    onSettled: () => setBusy(false),
  });

  const onPick = () => inputRef.current?.click();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    mutation.mutate(f);
    e.target.value = "";
  };

  return (
    <Card data-testid="knowledge-ingest-upload" className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg flex items-center gap-2"><FileUp className="h-5 w-5 text-emerald-500" /> Upload a document</CardTitle>
        <CardDescription>PDF, DOCX, TXT, or Markdown · up to 15 MB. The system extracts and proposes facts — you approve what enters the brain.</CardDescription>
      </CardHeader>
      <CardContent>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
          onChange={onChange}
          className="hidden"
          data-testid="knowledge-upload-input"
        />
        <Button
          data-testid="knowledge-upload-button"
          onClick={onPick}
          disabled={busy}
          className="w-full sm:w-auto"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}
          {busy ? "Parsing…" : "Choose a document"}
        </Button>
      </CardContent>
    </Card>
  );
}


function IngestUrlCard({ ws, onIngested }: { ws: string; onIngested: () => void }) {
  const [url, setUrl] = useState("");

  const mutation = useMutation({
    mutationFn: () => ingestUrl(ws, url.trim()),
    onSuccess: (r) => {
      toast.success(`Parsed ${r.chunks_produced} fact${r.chunks_produced === 1 ? "" : "s"} from ${new URL(r.source.url ?? url).hostname}`);
      setUrl("");
      onIngested();
    },
    onError: (e: Error) => toast.error(e.message || "URL fetch failed"),
  });

  const disabled = !/^https?:\/\/\S+\.\S+/i.test(url.trim()) || mutation.isPending;

  return (
    <Card data-testid="knowledge-ingest-url" className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-emerald-500" /> Learn from a URL</CardTitle>
        <CardDescription>Drop a page URL — the system extracts positioning, offers, and proof points for your review.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">URL</Label>
          <Input
            data-testid="knowledge-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://your-brand.com/about"
          />
        </div>
        <Button
          data-testid="knowledge-url-submit"
          onClick={() => mutation.mutate()}
          disabled={disabled}
        >
          {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Globe className="mr-2 h-4 w-4" />}
          {mutation.isPending ? "Parsing…" : "Learn from this URL"}
        </Button>
      </CardContent>
    </Card>
  );
}


function ApprovalQueue({ ws, chunks, loading, onChange }: { ws: string; chunks: KnowledgeChunk[]; loading: boolean; onChange: () => void }) {
  if (loading) {
    return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  }
  if (chunks.length === 0) {
    // Patch 31 · Empty-state action links. Each action is a native anchor
    // so keyboard + screen-reader users can invoke the same behaviour as
    // sighted users. `scrollToIngest*` scrolls into view without changing
    // history — the target cards live above this queue on the same page.
    const scrollToIngest = (which: "upload" | "url") => (e: React.MouseEvent) => {
      e.preventDefault();
      const target = document.querySelector(
        which === "upload" ? "[data-testid=knowledge-ingest-upload]" : "[data-testid=knowledge-ingest-url]"
      );
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      const trigger = target?.querySelector(
        which === "upload" ? "[data-testid=knowledge-upload-button]" : "[data-testid=knowledge-url-input]"
      ) as HTMLElement | null;
      trigger?.focus({ preventScroll: true });
    };
    return (
      <Card className="border-border/60 bg-card/70">
        <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="knowledge-queue-empty">
          <p>
            Nothing awaiting approval.{" "}
            <a
              href="#knowledge-ingest-upload"
              onClick={scrollToIngest("upload")}
              data-testid="knowledge-empty-upload-link"
              className="underline decoration-emerald-500/50 underline-offset-4 hover:text-emerald-300"
            >
              Upload a document
            </a>{" "}
            or{" "}
            <a
              href="#knowledge-ingest-url"
              onClick={scrollToIngest("url")}
              data-testid="knowledge-empty-url-link"
              className="underline decoration-emerald-500/50 underline-offset-4 hover:text-emerald-300"
            >
              add a URL
            </a>{" "}
            to start teaching your Marketing Department.
          </p>
          <p className="mt-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground/80">
            Nothing enters your Knowledge Brain without your explicit approval.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="knowledge-queue-list">
      {chunks.map((c) => (
        <ApprovalCard key={c._id} ws={ws} chunk={c} onChange={onChange} />
      ))}
    </div>
  );
}


function ApprovalCard({ ws, chunk, onChange }: { ws: string; chunk: KnowledgeChunk; onChange: () => void }) {
  const approve = useMutation({
    mutationFn: () => approveChunk(ws, chunk._id),
    onSuccess: () => { toast.success("Added to the Knowledge Brain."); onChange(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const reject = useMutation({
    mutationFn: () => rejectChunk(ws, chunk._id),
    onSuccess: () => { toast.success("Rejected."); onChange(); },
    onError: (e: Error) => toast.error(e.message),
  });
  const confidencePct = Math.round(chunk.parser_confidence * 100);
  const confidenceBand = confidencePct >= 75 ? "text-emerald-400" : confidencePct >= 50 ? "text-amber-400" : "text-rose-400";
  return (
    <Card data-testid={`knowledge-queue-card-${chunk._id}`} className="border-border/60 bg-card/70">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="uppercase tracking-widest text-[10px]">
            {KIND_LABEL[chunk.proposed_kind] ?? chunk.proposed_kind}
          </Badge>
          {chunk.section_hint && (
            <Badge variant="outline" className="text-[10px] text-muted-foreground">
              Section: {chunk.section_hint}
            </Badge>
          )}
          <span className={`ml-auto font-mono text-[10px] uppercase tracking-widest ${confidenceBand}`}>
            Parser confidence · {confidencePct}%
          </span>
        </div>
        <CardTitle className="mt-2 font-display text-base">{chunk.proposed_summary || "New knowledge chunk"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground/85 whitespace-pre-wrap" data-testid={`knowledge-queue-body-${chunk._id}`}>
          {chunk.raw_text.length > 600 ? `${chunk.raw_text.slice(0, 600)}…` : chunk.raw_text}
        </p>
        {chunk.proposed_key_facts.length > 0 && (
          <div className="space-y-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Key facts</div>
            <ul className="list-disc pl-5 text-xs text-muted-foreground">
              {chunk.proposed_key_facts.slice(0, 5).map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}
        {chunk.parser_rationale && (
          <div className="text-[11px] text-muted-foreground">
            <span className="font-mono uppercase tracking-widest">Why proposed:</span> {chunk.parser_rationale}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button
            data-testid={`knowledge-approve-${chunk._id}`}
            onClick={() => approve.mutate()}
            disabled={approve.isPending || reject.isPending}
            size="sm"
          >
            {approve.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Approve
          </Button>
          <Button
            data-testid={`knowledge-reject-${chunk._id}`}
            onClick={() => reject.mutate()}
            disabled={approve.isPending || reject.isPending}
            variant="outline"
            size="sm"
          >
            {reject.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <X className="mr-2 h-4 w-4" />}
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function EntriesList({ entries, loading }: { entries: KnowledgeEntry[]; loading: boolean }) {
  if (loading) return <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>;
  if (entries.length === 0) {
    return (
      <Card className="border-border/60 bg-card/70">
        <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="knowledge-entries-empty">
          No approved knowledge yet. Approve chunks from the queue above and they will appear here.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="knowledge-entries-list">
      {entries.map((e) => (
        <Card key={e.entry_id} data-testid={`knowledge-entry-${e.entry_id}`} className="border-border/60 bg-card/70">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="uppercase tracking-widest text-[10px]">{KIND_LABEL[e.kind]}</Badge>
              {e.is_edited && <Badge variant="outline" className="text-[10px] text-muted-foreground">Edited</Badge>}
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Relevance {Math.round(e.relevance_score * 100)}%
              </span>
            </div>
            <CardTitle className="mt-2 font-display text-base">{e.summary}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground line-clamp-4">{e.body_markdown}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


function SourcesList({ sources, loading }: { sources: { _id?: string; source_id?: string; kind: string; filename?: string | null; url?: string | null; status: string; chunks_produced: number; chunks_approved: number; chunks_rejected: number; created_at: string }[]; loading: boolean }) {
  if (loading) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  if (sources.length === 0) {
    return (
      <Card className="border-border/60 bg-card/70">
        <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="knowledge-sources-empty">
          No sources registered yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="knowledge-sources-list">
      {sources.map((s) => (
        <div key={s._id ?? s.source_id} className="flex items-center justify-between rounded-md border border-border/60 bg-card/60 px-4 py-3">
          <div className="flex items-center gap-3">
            {s.kind === "website" ? <Globe className="h-4 w-4 text-muted-foreground" /> : <FileUp className="h-4 w-4 text-muted-foreground" />}
            <div>
              <div className="text-sm">{s.filename ?? s.url ?? s.kind}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {s.status} · {s.chunks_produced} produced · {s.chunks_approved} approved
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
